/*
  Calendar days, as opposed to instants.

  The API sends two different shapes and they must not be parsed the same way. An instant
  (`scheduledFor`, `createdAt`) is a full ISO timestamp with an offset, and `new Date` on it
  is correct. A calendar day (`dueDate`, a figure's `periodStart`) is "2026-09-04" with no
  time and no zone, and `new Date("2026-09-04")` reads it as midnight UTC. Rendered west of
  Greenwich that is the evening of the 3rd, so every due date in the product displayed a day
  early and a commitment due today read as "was due yesterday".

  Splitting the string and building a local date is the fix. It is not a rounding
  preference: a due date is a day on the owner's calendar, not a moment in time, and it
  should read the same in Denver as it does in London.
*/

/** Parses a `YYYY-MM-DD` as local midnight. Falls back to `Date` for anything else. */
export function parseDay(iso: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!match) return new Date(iso)

  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
}

/** Local midnight today, for comparing a due date against "now" without the clock. */
export function today(): Date {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

/** Whole days from today to a calendar day. Negative is past. */
export function daysUntil(iso: string): number {
  return Math.round((parseDay(iso).getTime() - today().getTime()) / 86_400_000)
}

export function formatDay(iso: string, options?: Intl.DateTimeFormatOptions): string {
  return parseDay(iso).toLocaleDateString(
    undefined, options ?? { day: 'numeric', month: 'short' })
}

/*
  The weekday, because the owner thinks of the meeting as "Friday" and not as a date. Today
  and tomorrow are named outright: "putting this on the table Friday" reads as next week
  when Friday is this afternoon.

  An instant rather than a calendar day, so `new Date` on it is correct and parseDay is not
  what this wants. It lives here because three screens say the same sentence and the third
  copy would have been the one that drifted.
*/
export function meetingDay(iso: string): string {
  const when = new Date(iso)
  if (Number.isNaN(when.getTime())) return 'at the next meeting'

  const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const days = Math.round((midnight(when) - midnight(new Date())) / 86_400_000)

  if (days <= 0) return 'today'
  if (days === 1) return 'tomorrow'

  // The board sits weekly, so the weekday alone is unambiguous out to a full week.
  if (days <= 7) return when.toLocaleDateString(undefined, { weekday: 'long' })

  return when.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' })
}
